import React, { useContext } from 'react';
import { createPortal } from 'react-dom';
import { Container, Row, Col, Nav, NavItem, NavLink, Spinner } from '../../shim/reactstrap';
import { Link, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { AdminPortalContext } from '../../contexts/AdminPortalContext';
import { NavMenu } from '../NavMenu';
import { Footer } from '../Footer';
import { AdminUsers } from './AdminUsers';
import { AdminTickers } from './AdminTickers';
import { AdminCandles } from './AdminCandles';
import { AdminDataCleanup } from './AdminDataCleanup';
import { AdminExportImport } from './AdminExportImport';
import { AdminStatistics } from './AdminStatistics';
import { AdminSources } from './AdminSources';
import { AdminModelsAnalysis } from './AdminModelsAnalysis';
import { AdminOriginalDataPackage } from './AdminOriginalDataPackage';
import { AdminOriginalDataMigration } from './AdminOriginalDataMigration';
import { AdminSystem } from './AdminSystem';
import { AdminSchedulerLogs } from './AdminSchedulerLogs';
import { AdminAuditLogs } from './AdminAuditLogs';
import { AdminSchedulers } from './AdminSchedulers';
import { AdminCompareSignals } from './AdminCompareSignals';
import { AdminCompareSpreadsheets } from './AdminCompareSpreadsheets';
import { AdminNotificationTypes } from './AdminNotificationTypes';
import { AdminCustomMarketData } from './AdminCustomMarketData';
import { AdminPackageTickersStatus } from './AdminPackageTickersStatus';
import { AdminPackageTickerAnalyze } from './AdminPackageTickerAnalyze';
import { AdminPackageTickers } from './AdminPackageTickers';
import { AdminPackageTickersDatesValidation } from './AdminPackageTickersDatesValidation';
import { AdminPackageTickersValidation } from './AdminPackageTickersValidation';
import { AdminGeneratedPackages } from './AdminGeneratedPackages';
import { AdminUserAccess } from './AdminUserAccess';

export const Admin = () => {
  const { user, isAuthenticated, loading } = useAuth();
  // Admin pages render through this portal so they escape the centred
  // `<Container>` wrapper of the default Layout and span the full browser
  // width. If the portal target is missing (e.g. during SSR or tests)
  // fall back to rendering inline so the page still works.
  const portalTarget = useContext(AdminPortalContext);
  const renderTo = (node) =>
    portalTarget ? createPortal(node, portalTarget) : node;

  // Show loading spinner while checking authentication
  if (loading) {
    return renderTo(
      <Container className="mt-4">
        <div className="text-center py-5">
          <Spinner />
          <p className="mt-2">Loading...</p>
        </div>
      </Container>
    );
  }

  // Check if user has Admin role
  const isAdmin = isAuthenticated && user?.Roles?.includes('Admin');

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!isAdmin) {
    return renderTo(
      <Container className="mt-4">
        <div className="alert alert-danger">
          <h4>Access Denied</h4>
          <p>You do not have permission to access the admin area.</p>
        </div>
      </Container>
    );
  }

  return renderTo(
    <>
      <NavMenu />
      <div className="admin-shell mt-4">
        <Row>
        <Col md={2}>
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">Admin</h5>
            </div>
            <div className="card-body p-0">
              <Nav vertical>
                <NavItem>
                  <NavLink tag={Link} to="/admin/statistics">
                    Statistics
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/users">
                    Users
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/user-access">
                    User Access
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/notification-types">
                    Notification Types
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/sources">
                    Sources
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/tickers">
                    Tickers
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/candles">
                    Candles
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/models-analysis">
                    Models Analysis
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/compare-signals">
                    Compare Signals
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/compare-spreadsheets">
                    Compare Spreadsheets
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/data-cleanup">
                    Data Cleanup
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/export-import">
                    Export / Import
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/original-data-package">
                    Original Data Package
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/package-tickers">
                    Package Tickers
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/package-tickers-status">
                    Package Tickers status
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/package-tickers-dates-validation">
                    Package Tickers Dates Validation
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/package-tickers-validation">
                    Package tickers validation
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/generated-packages">
                    Generated Packages
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/original-data-migration">
                    Data Migration
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/system">
                    System
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/custom-market-data">
                    Custom Market Data
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/schedulers">
                    Schedulers
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/scheduler-logs">
                    Scheduler Logs
                  </NavLink>
                </NavItem>
                <NavItem>
                  <NavLink tag={Link} to="/admin/audit-logs">
                    Audit Logs
                  </NavLink>
                </NavItem>
              </Nav>
            </div>
          </div>
        </Col>
        <Col md={10}>
          <Routes>
            <Route index element={<Navigate to="/admin/statistics" replace />} />
            <Route path="statistics" element={<AdminStatistics />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="notification-types" element={<AdminNotificationTypes />} />
            <Route path="sources" element={<AdminSources />} />
            <Route path="tickers" element={<AdminTickers />} />
            <Route path="candles" element={<AdminCandles />} />
            <Route path="models-analysis" element={<AdminModelsAnalysis />} />
            <Route path="compare-signals" element={<AdminCompareSignals />} />
            <Route path="compare-spreadsheets" element={<AdminCompareSpreadsheets />} />
            <Route path="package-tickers" element={<AdminPackageTickers />} />
            <Route path="package-tickers-status" element={<AdminPackageTickersStatus />} />
            <Route path="package-tickers/:packageTickerId/analyze" element={<AdminPackageTickerAnalyze />} />
            <Route path="package-tickers-dates-validation" element={<AdminPackageTickersDatesValidation />} />
            <Route path="package-tickers-validation" element={<AdminPackageTickersValidation />} />
            <Route path="data-cleanup" element={<AdminDataCleanup />} />
            <Route path="export-import" element={<AdminExportImport />} />
            <Route path="original-data-package" element={<AdminOriginalDataPackage />} />
            <Route path="schedulers" element={<AdminSchedulers />} />
            <Route path="generated-packages" element={<AdminGeneratedPackages />} />
            <Route path="original-data-migration" element={<AdminOriginalDataMigration />} />
            <Route path="system" element={<AdminSystem />} />
            <Route path="custom-market-data" element={<AdminCustomMarketData />} />
            <Route path="scheduler-logs" element={<AdminSchedulerLogs />} />
            <Route path="audit-logs" element={<AdminAuditLogs />} />
            <Route path="user-access" element={<AdminUserAccess />} />
          </Routes>
        </Col>
      </Row>
      </div>
      <Footer />
    </>
  );
}
